import { INumberInputSchema } from '../../../../../types'
import { memo, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import {
    selectRuleConfigBit,
    selectRuleIsChecked,
} from '../../../../../../../store/selectors/ruleCardSelectors'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getLabel } from '../../../../../../../utils/getLabel'
import { initInputData } from '../../../../../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../../../../../utils/InputDataDispatcher'
import {
    buildControlsExt,
    integerRangeInput,
} from '../../../../../../inputs/controls'
import { integerRangeErrors } from '../../../../../../../utils/validationErrors/integers/integerRangeErrors'

interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: INumberInputSchema
    name: string
    wrapper?: string
}

export const RuleIntRange = memo(function RuleIntRange({
    dataPath,
    pathWithElementID,
    dataSchema,
    name,
    wrapper,
}: Props) {
    const dispatch = useAppDispatch()
    const pathSplit = dataPath.split(PATH_JOINER)
    const configBit = useAppSelector(selectRuleConfigBit(pathSplit))
    const path = [...pathSplit, name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectRuleIsChecked)
    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        default: defaultValue,
    } = dataSchema
    const validationCheck = inputValidationCheck(
        type,
        dataSchema,
        configBit?.[name]
    )
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck })

    const className = !wrapper ? 'w-100' : ''
    const label = getLabel(name, inputLabel)
    const errorKey = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined

    useEffect(() => {
        if (isChecked && dataPath.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    errorKey,
                    data: configBit,
                    setInputState,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    data: configBit,
                    name,
                    path,
                    dataSchema,
                    inputState,
                    validationCheck,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                data: configBit,
                name,
                path,
                dataSchema,
                inputState,
                validationCheck,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
        setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        integerRangeInput({
            name,
            tooltipText,
            label,
            validationType: type,
            className,
            isColumn: true,
            errMsg: integerRangeErrors(inputState?.[name], dataSchema),
            isRequired,
            placeholder: defaultValue,
            onBlur: handleOnBlur,
        }),
    ]

    const IntegerRangeInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return (
        <div className={`${!wrapper ? 'w-100' : ''}`}>{IntegerRangeInput}</div>
    )
})
